/**
 * Cache Service
 * Optimized caching with localStorage and sessionStorage
 */

const CACHE_PREFIX = 'magicstay_cache_';
const DEFAULT_TTL = 5 * 60 * 1000; // 5 minutes

/**
 * Cache entry structure
 * @typedef {Object} CacheEntry
 * @property {any} data - The cached data
 * @property {number} timestamp - When the data was cached
 * @property {number} ttl - Time to live in milliseconds
 */

export const cache = {
  /**
   * Set a value in cache
   * @param {string} key - Cache key
   * @param {any} data - Data to cache
   * @param {number} ttl - Time to live in ms (default: 5 min)
   * @param {boolean} session - Use sessionStorage instead of localStorage
   */
  set: (key, data, ttl = DEFAULT_TTL, session = false) => {
    try {
      const entry = {
        data,
        timestamp: Date.now(),
        ttl
      };
      
      const storage = session ? sessionStorage : localStorage;
      storage.setItem(CACHE_PREFIX + key, JSON.stringify(entry));
      return true;
    } catch (e) {
      console.warn('Cache set error:', e);
      return false;
    }
  },

  /**
   * Get a value from cache
   * @param {string} key - Cache key
   * @param {boolean} session - Use sessionStorage
   * @returns {any|null} - Cached data or null if expired/not found
   */
  get: (key, session = false) => {
    try {
      const storage = session ? sessionStorage : localStorage;
      const item = storage.getItem(CACHE_PREFIX + key);
      
      if (!item) return null;
      
      const entry = JSON.parse(item);
      const now = Date.now();
      
      // Check if expired
      if (now - entry.timestamp > entry.ttl) {
        storage.removeItem(CACHE_PREFIX + key);
        return null;
      }
      
      return entry.data;
    } catch (e) {
      console.warn('Cache get error:', e);
      return null;
    }
  },

  /**
   * Remove a value from cache
   * @param {string} key - Cache key
   * @param {boolean} session - Use sessionStorage
   */
  remove: (key, session = false) => {
    try {
      const storage = session ? sessionStorage : localStorage;
      storage.removeItem(CACHE_PREFIX + key);
      return true;
    } catch (e) {
      console.warn('Cache remove error:', e);
      return false;
    }
  },

  /**
   * Clear all cached items
   * @param {boolean} session - Use sessionStorage
   */
  clear: (session = false) => {
    try {
      const storage = session ? sessionStorage : localStorage;
      const keys = Object.keys(storage).filter(k => k.startsWith(CACHE_PREFIX));
      keys.forEach(k => storage.removeItem(k));
      return true;
    } catch (e) {
      console.warn('Cache clear error:', e);
      return false;
    }
  },

  /**
   * Clear expired items from cache
   */
  clearExpired: () => {
    try {
      const now = Date.now();
      
      [localStorage, sessionStorage].forEach(storage => {
        const keys = Object.keys(storage).filter(k => k.startsWith(CACHE_PREFIX));
        
        keys.forEach(key => {
          try {
            const item = storage.getItem(key);
            if (item) {
              const entry = JSON.parse(item);
              if (now - entry.timestamp > entry.ttl) {
                storage.removeItem(key);
              }
            }
          } catch (e) {
            // Remove corrupted entries
            storage.removeItem(key);
          }
        });
      });
      
      return true;
    } catch (e) {
      console.warn('Cache clearExpired error:', e);
      return false;
    }
  },

  /**
   * Get or set pattern - returns cached value or fetches and caches
   * @param {string} key - Cache key
   * @param {Function} fetcher - Async function to fetch data if not cached
   * @param {number} ttl - Time to live
   * @param {boolean} session - Use sessionStorage
   */
  getOrSet: async (key, fetcher, ttl = DEFAULT_TTL, session = false) => {
    const cached = cache.get(key, session);
    if (cached !== null) return cached;
    
    const data = await fetcher();
    cache.set(key, data, ttl, session);
    return data;
  },

  /**
   * Get cache stats
   */
  getStats: () => {
    const stats = {
      localStorage: { count: 0, size: 0 },
      sessionStorage: { count: 0, size: 0 },
      total: { count: 0, size: 0 }
    };

    try {
      [localStorage, sessionStorage].forEach((storage, idx) => {
        const type = idx === 0 ? 'localStorage' : 'sessionStorage';
        const keys = Object.keys(storage).filter(k => k.startsWith(CACHE_PREFIX));
        
        keys.forEach(key => {
          const item = storage.getItem(key);
          if (item) {
            stats[type].count++;
            stats[type].size += item.length * 2; // UTF-16 chars = 2 bytes
          }
        });
        
        stats.total.count += stats[type].count;
        stats.total.size += stats[type].size;
      });
    } catch (e) {
      console.warn('Cache getStats error:', e);
    }

    return stats;
  }
};

// Periodically clear expired cache entries (every 5 minutes)
if (typeof window !== 'undefined') {
  setInterval(() => {
    cache.clearExpired();
  }, 5 * 60 * 1000);
}

export default cache;
